import { createPayload } from '@/utils/createPayload';
import { toEmployeePayload } from '@/utils/data/toEmployeePayload';
import { reportSilentError } from '@/utils/storeErrorReporting';
import { API_DOMAIN, authHeaders, parseResponse } from '@/api/headers';

/**
 * Returns a list of volunteers, given some URL parameters.
 * This can be used by ANY user.
 * If you are looking for a recommendation engine for educators, use `fetchVolunteers` in `@/api/volunteers`.
 * @param {URLSearchParams} [params] - Pre-built query params (e.g. `limit`, `group_id`, filters).
 */
export const fetchAllVolunteers = async (params = new URLSearchParams()) => {
    try {
        const { token } = await createPayload(null);
        const query = new URLSearchParams(params).toString();
        const endpointURL = `${API_DOMAIN}/employees${query ? `?${query}` : ''}`;

        const response = await fetch(endpointURL, { method: 'GET', headers: authHeaders(token) });
        return await parseResponse(response);
    } catch (error) {
        reportSilentError(error);
        return undefined;
    }
};

/**
 * Returns a list of volunteers from a specific organization.
 * Can only be used by Org Admin because this lists inactive volunteers that
 * are not shown to the public.
 * @param {organizationID} string
 * @param {URLSearchParams} [params] - Pre-built query params (e.g. `limit`, `group_id`, filters).
 */
export const fetchOrganizationVolunteers = async (organizationID, params = new URLSearchParams()) => {
    try {
        const { token } = await createPayload(null);

        if (typeof organizationID !== 'string') {
            throw new Error('OrganizationID is invalid');
        }
        const query = new URLSearchParams(params).toString();

        const endpointURL = `${API_DOMAIN}/organizations/${organizationID}/employees${query ? `?${query}` : ''}`;

        const response = await fetch(endpointURL, { method: 'GET', headers: authHeaders(token) });
        return await parseResponse(response);
    } catch (error) {
        reportSilentError(error);
        return undefined;
    }
};

/**
 * Saves an employee record to the BE with `isActive: false`.
 * @param {string} employeeId
 * @param {Object} employeeData - Current employee data; sent back with `isActive` overridden.
 * @param {string} token
 * @returns {Object} the updated employee from the API
 * @throws {Error} on a non-2xx response
 */
export const deactivateEmployee = async (employeeId, employeeData, token) => {
    const response = await fetch(`${API_DOMAIN}/employees/${employeeId}`, {
        method: 'POST',
        body: JSON.stringify({ ...toEmployeePayload(employeeData), isActive: false }),
        headers: authHeaders(token),
    });

    return parseResponse(response);
};
